/**
 * Tryout home API contracts for the authenticated event destination page.
 */

import { apiClient } from './client'
import type {StaffNounEvent} from '@/utils/staffNouns'
import type { ClubHomeEventRoles } from './club'
import type { TryoutSetupClub, TryoutSetupSeason, TryoutSetupValidationIssue } from './tryoutSetup'

export interface TryoutHomeEvent extends StaffNounEvent {
  id: string
  seasonId: string
  title: string
  description?: string
  eventNoun?: string
  sessionNoun?: string
  /** @deprecated Compatibility alias; use participantNoun. */
  participantPronoun?: string
  participantNoun?: string
  recommendationLabels?: import('@/utils/recommendationLabels').RecommendationTargetLabels
  startDate?: string
  endDate?: string
  location?: string
  allowJournal?: boolean
  participantCount?: number
  selectorCount?: number
  active?: boolean
  roles?: ClubHomeEventRoles & {
    sysadmin?: boolean
  }
}

export interface TryoutHomeStats {
  participantCount?: number
  selectorCount?: number
  sessionsRemaining?: number
}

export interface TryoutHomeSession {
  id: string
  title: string
  startTime?: string
  endTime?: string
  startDateTime?: string
  endDateTime?: string
  venue?: string
  venueName?: string
  court?: string
  address?: string
  location?: string
}

export interface TryoutHomeCoverage {
  ZERO?: number
  NOK?: number
  OK?: number
  complete?: number
}

export interface TryoutHomeNotification {
  id?: string
  type: 'feedback' | 'data-warning' | 'system' | string
  message: string
  severity?: 'info' | 'warning' | 'error'
}

export interface TryoutHomeSetupCheck {
  key: 'details' | 'contact' | 'rubric' | 'roles' | 'staff' | 'sessions' | 'questions' | 'teams' | 'attributes'
  panel: string
  complete: boolean
  required: boolean
}

export interface TryoutHomeReadiness {
  checks?: TryoutHomeSetupCheck[]
  collection?: { hasSessions: boolean; hasParticipants: boolean }
  completionRate: number
  missing: string[]
  issues?: TryoutSetupValidationIssue[]
}

export interface TryoutHomeActionRequirement {
  key: string
  label: string
  requiredEndpoint: string
  responseShape: string
}

export interface HistoricalEventEvidence {
  observationCount: number
  participantCount: number
  criterionCount: number
}

export interface TryoutHomeResponse {
  historicalEvidence?: HistoricalEventEvidence
  features?: import('./programSettings').ProgramFeatures
  club: TryoutSetupClub
  season: TryoutSetupSeason
  event: TryoutHomeEvent
  sessions: TryoutHomeSession[]
  stats?: TryoutHomeStats
  readiness?: TryoutHomeReadiness
  coverage?: TryoutHomeCoverage
  notifications?: TryoutHomeNotification[]
  unsupportedActions?: TryoutHomeActionRequirement[]
}

export class TryoutHomeAPI {
  async loadHome(
    seasonId: string,
    tryoutId: string,
    clubId?: string,
    signal?: AbortSignal
  ): Promise<TryoutHomeResponse> {
    const query = clubId ? { clubId } : undefined
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/home`, query, signal)
  }
}

export const tryoutHomeAPI = new TryoutHomeAPI()
